/**
 * @file goldRechargeApi.js
 * @description 用户中心橘子币档位与微信预下单 / 查单
 * @module features/user/api/goldRechargeApi
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'
import { getAuthToken } from '@/shared/utils/auth'
import { ERR_WX_MP_OAUTH_REQUIRED, normalizeApiCode } from '@/shared/utils/apiErrorCodes'
import { handleWxMpOAuthRequiredResponse, getOAuthRedirectPage } from '@/shared/utils/wxMpOAuth'

/**
 * JSAPI 预下单缺 openid（prepare code=10102）
 */
export class GoldWxMpOAuthRequiredError extends Error {
  /**
   * @param {string} [oauthAuthorizeUrl]
   * @param {string} [message]
   */
  constructor(oauthAuthorizeUrl = '', message) {
    super(message || '请先完成微信授权后再充值')
    this.name = 'GoldWxMpOAuthRequiredError'
    this.code = ERR_WX_MP_OAUTH_REQUIRED
    this.oauthAuthorizeUrl = typeof oauthAuthorizeUrl === 'string' ? oauthAuthorizeUrl : ''
  }
}

/**
 * @returns {Record<string, string>}
 */
function buildOAuthRedirectHeader() {
  const page = getOAuthRedirectPage()
  if (!page) return {}
  return { 'X-OAuth-Redirect': page }
}

/**
 * @returns {import('axios').AxiosRequestConfig}
 */
function goldAuthConfig() {
  return {
    headers: {
      'COMIC-WEB-TOKEN': getAuthToken(),
      'Content-Type': 'application/json',
      ...buildOAuthRedirectHeader(),
    },
  }
}

/**
 * 用户中心橘子币档位列表（无差额、无推荐档）
 * @returns {Promise<import('./goldRechargeTypes').GoldPackageListVo>}
 */
export async function fetchGoldPackageListApi() {
  const { data } = await axios.post(API.PAY.GOLD_PACKAGE_LIST, {}, goldAuthConfig())
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '获取充值档位失败'), {
      code: data.code,
    })
  }
  return data.data
}

/**
 * 橘子币微信预下单
 * @param {import('./goldRechargeTypes').PrepareGoldWechatPayload} payload
 * @returns {Promise<import('./goldRechargeTypes').PrepareGoldWechatVo>}
 */
export async function prepareGoldWechatApi(payload) {
  const { data } = await axios.post(API.PAY.GOLD_WECHAT_PREPARE, payload, goldAuthConfig())
  if (normalizeApiCode(data?.code) === ERR_WX_MP_OAUTH_REQUIRED) {
    if (handleWxMpOAuthRequiredResponse(data)) {
      return new Promise(() => {})
    }
    const oauthAuthorizeUrl =
      data.data && typeof data.data.oauthAuthorizeUrl === 'string'
        ? data.data.oauthAuthorizeUrl
        : ''
    throw new GoldWxMpOAuthRequiredError(oauthAuthorizeUrl, data.msg)
  }
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '创建充值订单失败'), {
      code: data.code,
    })
  }
  return data.data
}

/**
 * 橘子币微信查单
 * @param {{ orderId: string }} payload
 * @returns {Promise<import('./goldRechargeTypes').GoldOrderStatusVo>}
 */
export async function queryGoldWechatOrderStatusApi(payload) {
  const { data } = await axios.post(API.PAY.GOLD_WECHAT_ORDER_STATUS, payload, goldAuthConfig())
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '查询订单失败'), {
      code: data.code,
    })
  }
  return data.data
}
